import gsap from "gsap";
import { SplitText } from "gsap/src/SplitText";
import { ScrollTrigger } from "gsap/src/ScrollTrigger";
import { DrawSVGPlugin } from "gsap/src/DrawSVGPlugin";

gsap.registerPlugin(SplitText, ScrollTrigger, DrawSVGPlugin);


function createParallaxEffect() {
  gsap.utils.toArray('.parallax-container').forEach(container => {

    const img = container.querySelector('img');
  
    // Create a GSAP timeline for the image with a ScrollTrigger
    gsap.timeline({
      scrollTrigger: {
        trigger: container,
        start: "top bottom", // When the top of the container enters the bottom of the viewport
        end: "bottom top", // When the bottom of the container exits the top of the viewport
        scrub: 1 // Smooth out the animation's beginning and end (adjust the number for more/less smoothing)
      }
    })
    .fromTo(img, {
      y: '-6%' // Start the image slightly above its normal position
    }, {
      y: '0%', // End with the image slightly below its normal position
      ease: "none" // Keep the movement linear
    });
  });
}


// Pinned sections
function pinnedSections() {
    // Convert all elements with class '.pinned-section' into an array
    const sections = gsap.utils.toArray(".pinned-section");
  
    // Loop through each section
    sections.forEach(section => {
      // Select the text and image elements within the current section
      const text = section.querySelector(".text"),
            image = section.querySelector(".img");
  
      // Create a timeline with a ScrollTrigger linked to each section
      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: section,   // The current section is the trigger
          pin: true,         // Pin the section during the scroll
          anticipatePin: 1,  // Prepare for pinning 1 tick in advance
          scrub: 1,          // Link the animation progress to the scroll progress
          start: "top top",  // Start when the top of the trigger hits the top of the viewport
          end: "+=500"       // End after scrolling 500 pixels
        }
      });
  
      // Animate the text opacity and position from the bottom to center
      tl.from(text, {
        opacity: 0,
        y: 100
      }, 0) // 0 sets the position of the tween at the start of the timeline
        // Animate the image padding from 10% to 0 and scale the image up
        .to(image, {
          padding: 0,
          ease: "power1.inOut",
          duration: 1,
          onStart: () => {
            gsap.set(image, {scale: 1}); // Ensure image is scaled to full size
          }
        }, 0) // Sync this animation with the text animation
        // Fade the text out and move upwards towards the end of the scroll
        .to(text, {
          opacity: 0,
          y: -100,
          ease: "power1.in"
        });
    });
  }
  

  function animateText() {
    let resizeTimer; // Timer identifier for debouncing
  
    const elements = document.querySelectorAll(".animate-text");
  
    // Cleanup function to remove animation wrappers
    function cleanupAnimations() {
      elements.forEach(element => {
        const wrappers = element.querySelectorAll('div');
        wrappers.forEach(wrapper => {
          while (wrapper.firstChild) {
            wrapper.parentNode.insertBefore(wrapper.firstChild, wrapper);
          }
          wrapper.parentNode.removeChild(wrapper);
        });
      });
    }
  
    // Function containing the core animation logic
    function initializeAnimations() {
      elements.forEach(element => {
        const animationType = element.getAttribute("data-animation-type") || "lines";
        const ease = element.getAttribute("data-ease") || "power4.out";
        const delay = parseFloat(element.getAttribute("data-delay")) || 0.5;
        const duration = parseFloat(element.getAttribute("data-duration")) || 0.5;
        const staggerVal = parseFloat(element.getAttribute("data-stagger")) || 0.1;
        const clipAnimation = element.getAttribute("data-clip-animation") === "true";
  
        if (animationType === "lines" || animationType === "words") {
          const split = new SplitText(element, {
            type: animationType,
            linesClass: "line-child",
            wordsClass: "word-child"
          });
  
          let targets = animationType === "lines" ? split.lines : split.words;
  
          if (clipAnimation) {
            targets.forEach((target, i) => {
              target.style.opacity = 0;
              target.style.transform = 'translateY(100%)';
  
              const wrapper = document.createElement('div');
              wrapper.style.overflow = 'hidden';
              wrapper.style.paddingBottom = '0.1em';
              if (animationType === "words") {
                wrapper.style.display = 'inline-block';
              }
  
              target.parentNode.insertBefore(wrapper, target);
              wrapper.appendChild(target);
  
              let elementDelay = delay + i * staggerVal;
  
              gsap.to(target, {
                duration: duration,
                y: '0%',
                opacity: 1,
                ease: ease,
                delay: elementDelay,
                scrollTrigger: {
                  trigger: element,
                  start: "top bottom-=100",
                  end: "bottom top",
                  toggleActions: "play none none none"
                }
              });
            });
          } else {
            gsap.from(targets, {
              duration: duration,
              yPercent: 100,
              opacity: 0,
              ease: ease,
              delay: delay,
              stagger: staggerVal,
              scrollTrigger: {
                trigger: element,
                start: "top bottom-=100",
                end: "bottom top",
                toggleActions: "play none none none"
              }
            });
          }
        } else {
          gsap.from([element], {
            duration: duration,
            yPercent: 0,
            opacity: 0,
            ease: ease,
            delay: delay,
            stagger: {
              amount: staggerVal
            },
            scrollTrigger: {
              trigger: element,
              start: "top bottom-=100",
              end: "bottom top",
              toggleActions: "play none none none"
            }
          });
        }
      });
    }
  
    // Resize event listener with debouncing
    if (!('ontouchstart' in window) || navigator.maxTouchPoints === 0) {
        window.addEventListener('resize', () => {
          clearTimeout(resizeTimer);
          resizeTimer = setTimeout(() => {
            cleanupAnimations(); // Remove existing animations and wrappers
            initializeAnimations(); // Reinitialize animations
          }, 10); // Adjust debounce time as needed
        });
      }
  
    // Initial call to set up animations
    initializeAnimations();
  }


  function animateImageSwipeIn() {
    const containers = gsap.utils.toArray(".swipe-container, .project-image");
  
    containers.forEach((container) => {
      // Ensure the container clips the content
      gsap.set(container, { overflow: 'hidden' });
  
      // Select the image within the container
      const image = container.querySelector("img");
  
      // Initialize the image offscreen to the left and slightly zoomed in
      gsap.set(image, { yPercent: 0, scale: 1, opacity: 1 });
  
      // Animate the image to swipe in from the left and scale down to normal size
      gsap.to(image, {
        yPercent: 0, // Bring the image back to its original horizontal position
        scale: 1.03,   // Scale down to the original size
        duration: 1.3, // Duration of the swipe-in and scale-down animation
        ease: "power2.out", // Smooth easing function for the animation
        scrollTrigger: {
          trigger: container, // The animation triggers when the parent container enters the view
          start: "top 70%", // Start the animation when the top of the container hits 90% of the viewport
          end: "bottom top", // End when the bottom of the container exits the top of the viewport
          toggleActions: "play none none none", // Play the animation once when the container enters the viewport
          markers: false // Enable during development to see the trigger points
        }
      });
    });
  }
  

  function initializeAnimation() {


    // Register the DrawSVGPlugin necessary for SVG drawing animations
    gsap.registerPlugin(DrawSVGPlugin);

    // Define a color array for the color change interactions
    const colorArray = ["#ffffff", "#46a4cc", "#94c356", "#eae047", "#e3aa59", "#a63ba0", "#cf5b21"];

    // Select all circle elements to be used as color swatches
    let colorSwatches = document.querySelectorAll("circle");

    // Initialize GSAP timeline with default ease settings
    const tl = gsap.timeline({ defaults: { ease: "power2.inOut" } });

    // Set initial states for SVG elements
    gsap.set("#theBurger", { autoAlpha: 1 });
    gsap.set(".buns", { drawSVG: "0% 30%" });
    // gsap.set(".letters", { drawSVG: "53.5% 100%", x: -155 });

    // Apply initial colors to the swatches and set up click listeners
    for (let i = 0; i < colorSwatches.length; i++) {
        gsap.set(colorSwatches[i], { fill: colorArray[i] });
        colorSwatches[i].index = i;  // Store index on the element for later reference
        colorSwatches[i].addEventListener("click", colorChange);
    }

    // Define the timeline for the burger animation
    tl.to(".patty", { duration: 0.35, drawSVG: "50% 50%" }, 0)
      .to(".patty", { duration: 0.1, opacity: 0, ease: "none" }, 0.25)
      .to(".buns", { duration: 0.85, drawSVG: "69% 96.5%" }, 0)
      // .to(".letters", { duration: 0.85, drawSVG: "0% 53%", x: 0 }, 0)
      .reversed(true); // Start with the animation reversed

    // Function to handle color changes on swatch click
    function colorChange() {
        gsap.to("#burger", 0.5, {
            stroke: colorArray[this.index],
            ease: "none"
        });
    }

    // Add click event listener to trigger the main burger animation
    document.querySelector("#theBurger").addEventListener("click", function animateTheBurger() {
        tl.reversed(!tl.reversed());
    });
}




export default {
    init: function() {
        animateText();
        pinnedSections();
        createParallaxEffect();
        animateImageSwipeIn();
        initializeAnimation();
    }
};
